import { useEffect, useState, useRef } from 'react';
import { ChatBubble } from './ChatBubble';
import { ChatFooter } from './ChatFooter';
import { ChatHeader } from './ChatHeader';
import { ChatbotTrendingTopics } from './ChatbotTrendingTopics';
import './chatbot.styles.css';
import PropTypes from 'prop-types';

import { IoIosArrowDown } from 'react-icons/io';

import { useChatbotStore } from '@stores';
import { userManager } from '@providers/userManager';

/**
 * Main chatbot component handling conversation UI and logic
 * Manages messages, trending topics, and user interactions
 */
export default function Chatbot({ toggleChatbotHandler }) {
    const chatContainerRef = useRef(null);
    const trendingTopicsRef = useRef(null);

    const [toggleTrendingTopics, setToggleTrendingTopics] = useState(false);

    // Track messages and loading state
    const { messages, initializeChat, isLoading } = useChatbotStore();
    const { trendingTopics, getTrendingTopics } = useChatbotStore();

    const scrollToBottom = () => {
        if (chatContainerRef.current) {
            chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
        }
    };

    useEffect(() => {
        const userInfo = userManager.getUserData();

        initializeChat(null, userInfo);
        getTrendingTopics();
    }, [getTrendingTopics, initializeChat]);

    /**
     * Auto-scrolls chat to bottom when new messages arrive
     */
    useEffect(() => {
        scrollToBottom();
    }, [messages, isLoading]);

    useEffect(() => {
        const handleClickOutside = event => {
            if (trendingTopicsRef.current && !trendingTopicsRef.current.contains(event.target)) {
                setToggleTrendingTopics(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    return (
        <div className=" w-full h-full rounded-[10px] overflow-hidden bg-white shadow-card">
            <ChatHeader toggleChatbotHandler={toggleChatbotHandler} />
            <div
                ref={chatContainerRef}
                className="chat-container h-[calc(100vh_-_210px)] overflow-auto py-4 mx-4 flex flex-col justify-between"
            >
                <div className="chat-messages">
                    {messages &&
                        messages.length > 0 &&
                        messages.map((msg, index) => (
                            <ChatBubble
                                key={index}
                                sender={msg.sender}
                                message={msg.message}
                                messageId={msg.id}
                            />
                        ))}

                    {isLoading && <ChatBubble sender="bot" message="Loading..." isLoading />}
                </div>
                <div className="trending-topics sticky bottom-0" ref={trendingTopicsRef}>
                    {trendingTopics && trendingTopics.length > 0 && (
                        <>
                            {toggleTrendingTopics && (
                                <ChatbotTrendingTopics
                                    trendingTopics={trendingTopics}
                                    setToggleTrendingTopics={setToggleTrendingTopics}
                                />
                            )}

                            <div
                                className="bg-primary-blue flex items-center justify-center px-2 py-1 rounded-xl cursor-pointer hover:bg-blue-800 text-xs text-white w-fit mx-auto mt-2"
                                onClick={() => setToggleTrendingTopics(!toggleTrendingTopics)}
                            >
                                Trending Topics{' '}
                                <IoIosArrowDown className="inline-block ml-1 text-base" />
                            </div>
                        </>
                    )}
                </div>
            </div>
            <ChatFooter />
        </div>
    );
}

Chatbot.propTypes = {
    toggleChatbotHandler: PropTypes.func.isRequired,
};
